import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth0 } from '@auth0/auth0-react'
import { toast } from 'sonner'
import { useSupabase } from '@/lib/supabase-context'
import { useOrganizacion } from '@/core/providers/OrganizacionProvider'
import { usePermissions } from '@/hooks/usePermissions'
import { assertPermiso, handleMutationError } from '@/lib/permisos-guard'
import type { Periodo } from '@/lib/periodo-reporte'
import { instagramService, type PublicacionParaMail } from '@/services/instagramService'

export const instagramKeys = {
  all: ['instagram'] as const,
  cuenta: (orgId?: string | null) => [...instagramKeys.all, 'cuenta', orgId] as const,
  rendimientoTodos: () => [...instagramKeys.all, 'rendimiento'] as const,
  rendimiento: (orgId: string | null | undefined, periodo: Periodo) =>
    [...instagramKeys.rendimientoTodos(), orgId, periodo] as const,
  serie: (orgId: string | null | undefined, periodo: Periodo) => [...instagramKeys.all, 'serie', orgId, periodo] as const,
  paraMails: (orgId: string | null | undefined, cuentaId: string | null | undefined) =>
    [...instagramKeys.all, 'para-mails', orgId, cuentaId] as const,
  paraMailsTodas: () => [...instagramKeys.all, 'para-mails'] as const,
  clicsMails: (orgId: string | null | undefined, periodo: Periodo) => [...instagramKeys.all, 'clics-mails', orgId, periodo] as const,
  sinPromoCliente: (orgId: string | null | undefined, clienteId: string | null | undefined) =>
    [...instagramKeys.all, 'sin-promo-cliente', orgId, clienteId] as const,
  seguidores: (orgId: string | null | undefined, cuentaId: string | null | undefined) =>
    [...instagramKeys.all, 'seguidores', orgId, cuentaId] as const,
}

export function useInstagramCuenta() {
  const { supabase, isLoading } = useSupabase()
  const { organizacionId } = useOrganizacion()

  return useQuery({
    queryKey: instagramKeys.cuenta(organizacionId),
    queryFn: () => instagramService.obtenerCuenta(supabase!, organizacionId!),
    enabled: !!supabase && !isLoading && !!organizacionId,
  })
}

/** Último conteo de seguidores de la cuenta activa: lo muestra la vista previa del bloque de los mails. */
export function useSeguidoresInstagram(cuentaId: string | null | undefined) {
  const { supabase, isLoading } = useSupabase()
  const { organizacionId } = useOrganizacion()

  return useQuery({
    queryKey: instagramKeys.seguidores(organizacionId, cuentaId),
    queryFn: () => instagramService.ultimosSeguidores(supabase!, organizacionId!, cuentaId!),
    enabled: !!supabase && !isLoading && !!organizacionId && !!cuentaId,
  })
}

/** Pide la URL de Instagram y navega hacia ella. */
export function useIniciarInstagram() {
  const { getAccessTokenSilently } = useAuth0()
  const { organizacionId } = useOrganizacion()

  return useMutation({
    mutationFn: async () => {
      if (!organizacionId) throw new Error('Organización no disponible')
      const { url } = await instagramService.iniciar(await getAccessTokenSilently(), organizacionId)
      window.location.assign(url)
    },
    onError: (error: Error) => toast.error(error.message),
  })
}

export function useCanjearInstagram() {
  const queryClient = useQueryClient()
  const { getAccessTokenSilently } = useAuth0()
  const { organizacionId } = useOrganizacion()

  return useMutation({
    mutationFn: async ({ code, state }: { code: string; state: string }) => {
      if (!organizacionId) throw new Error('Organización no disponible')
      return instagramService.canjear(await getAccessTokenSilently(), organizacionId, code, state)
    },
    onSuccess: ({ username }) => {
      queryClient.invalidateQueries({ queryKey: instagramKeys.all })
      toast.success(`Instagram conectado: @${username}`)
    },
    onError: (error: Error) => toast.error(error.message),
  })
}

/** Rendimiento de las publicaciones del período. Corre solo con verReportes. */
export function useRendimientoInstagram(periodo: Periodo) {
  const { supabase, isLoading } = useSupabase()
  const { organizacionId } = useOrganizacion()
  const { puede } = usePermissions()

  return useQuery({
    queryKey: instagramKeys.rendimiento(organizacionId, periodo),
    queryFn: () => instagramService.rendimientoPublicaciones(supabase!, organizacionId!, periodo),
    enabled: puede.verReportes === true && !!supabase && !isLoading && !!organizacionId,
  })
}

/** Clics desde los mails por tipo de mail en el período. Corre solo con verReportes. */
export function useClicsDesdeMails(periodo: Periodo) {
  const { getAccessTokenSilently } = useAuth0()
  const { organizacionId } = useOrganizacion()
  const { puede } = usePermissions()

  return useQuery({
    queryKey: instagramKeys.clicsMails(organizacionId, periodo),
    queryFn: async () => instagramService.clicsDesdeMails(await getAccessTokenSilently(), organizacionId!, periodo),
    enabled: puede.verReportes === true && !!organizacionId,
  })
}

/** Altas, bajas y seguidores de la cuenta por día. Corre solo con verReportes. */
export function useSerieCuentaInstagram(periodo: Periodo) {
  const { supabase, isLoading } = useSupabase()
  const { organizacionId } = useOrganizacion()
  const { puede } = usePermissions()

  return useQuery({
    queryKey: instagramKeys.serie(organizacionId, periodo),
    queryFn: () => instagramService.serieCuenta(supabase!, organizacionId!, periodo),
    enabled: puede.verReportes === true && !!supabase && !isLoading && !!organizacionId,
  })
}

/** Publicaciones que pueden aparecer en el bloque de Instagram de los mails. Corre solo con verConfiguracion. */
export function usePublicacionesParaMails(cuentaId: string | null | undefined) {
  const { supabase, isLoading } = useSupabase()
  const { organizacionId } = useOrganizacion()
  const { puede } = usePermissions()

  return useQuery({
    queryKey: instagramKeys.paraMails(organizacionId, cuentaId),
    queryFn: () => instagramService.publicacionesParaMails(supabase!, organizacionId!, cuentaId!),
    enabled: puede.verConfiguracion === true && !!supabase && !isLoading && !!organizacionId && !!cuentaId,
  })
}

/** Si el cliente pidió no ver el bloque de Instagram en sus mails. */
export function useClienteSinPromoMails(clienteId: string | null | undefined) {
  const { supabase, isLoading } = useSupabase()
  const { organizacionId } = useOrganizacion()

  return useQuery({
    queryKey: instagramKeys.sinPromoCliente(organizacionId, clienteId),
    queryFn: () => instagramService.clienteSinPromoMails(supabase!, organizacionId!, clienteId!),
    enabled: !!supabase && !isLoading && !!organizacionId && !!clienteId,
  })
}

/** Marca o desmarca el opt-out del cliente. Es un atributo del cliente: exige editarCliente. */
export function useGuardarClienteSinPromoMails() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { organizacionId } = useOrganizacion()
  const { puede } = usePermissions()

  return useMutation({
    mutationFn: ({ clienteId, sinPromo }: { clienteId: string; sinPromo: boolean }) => {
      assertPermiso(puede, 'editarCliente')
      if (!supabase || !organizacionId) throw new Error('Organización no disponible')
      return instagramService.guardarClienteSinPromoMails(supabase, organizacionId, clienteId, sinPromo)
    },
    onSuccess: (_data, { clienteId }) =>
      queryClient.invalidateQueries({ queryKey: instagramKeys.sinPromoCliente(organizacionId, clienteId) }),
    onError: (error) => handleMutationError(error, 'No se pudo guardar la preferencia del cliente'),
  })
}

/** Saca una publicación del bloque de los mails o la vuelve a poner. Es configuración: exige editarConfiguracion. */
export function useOcultarEnMailsInstagram() {
  const queryClient = useQueryClient()
  const { getAccessTokenSilently } = useAuth0()
  const { organizacionId } = useOrganizacion()
  const { puede } = usePermissions()

  return useMutation({
    mutationFn: async ({ publicacionId, ocultar }: { publicacionId: string; ocultar: boolean }) => {
      assertPermiso(puede, 'editarConfiguracion')
      if (!organizacionId) throw new Error('Organización no disponible')
      return instagramService.ocultarEnMails(await getAccessTokenSilently(), organizacionId, publicacionId, ocultar)
    },
    // El interruptor se mueve al tocarlo y vuelve si la function falla: varios toques seguidos no se pierden.
    onMutate: async ({ publicacionId, ocultar }) => {
      const key = instagramKeys.paraMailsTodas()
      await queryClient.cancelQueries({ queryKey: key })
      const previas = queryClient.getQueriesData<PublicacionParaMail[]>({ queryKey: key })
      queryClient.setQueriesData<PublicacionParaMail[]>({ queryKey: key }, (actuales) =>
        actuales?.map((p) => (p.id === publicacionId ? { ...p, ocultar_en_mails: ocultar } : p)),
      )
      return { previas }
    },
    onError: (error, _variables, contexto) => {
      contexto?.previas.forEach(([key, data]) => queryClient.setQueryData(key, data))
      handleMutationError(error, 'No se pudo cambiar si la publicación va en los mails')
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: instagramKeys.paraMailsTodas() }),
  })
}

/** Guarda o borra el tema de una publicación. Exige verReportes. */
export function useGuardarTemaInstagram() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { organizacionId } = useOrganizacion()
  const { puede } = usePermissions()

  return useMutation({
    mutationFn: ({ publicacionId, tema }: { publicacionId: string; tema: string | null }) => {
      assertPermiso(puede, 'verReportes')
      if (!supabase || !organizacionId) throw new Error('Organización no disponible')
      return instagramService.guardarTema(supabase, organizacionId, publicacionId, tema)
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: instagramKeys.rendimientoTodos() }),
    onError: (error) => handleMutationError(error, 'No se pudo guardar el tema'),
  })
}
